import { Image } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { useLocation } from "react-router-dom";
import { UnauthLayout } from "layouts/UnauthLayout";
import { useBrand } from "brand/BrandConfig";
import { getMagicLinkErrorMessage } from "utils/magicLinkError";
import { useLoginForm, wantsForgotForm } from "./useLoginForm";
import { MagicLinkForm } from "./MagicLinkForm";
import { PasswordForm } from "./PasswordForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { defaultLoginUI, LoginUI } from "./loginUI";

export type { LoginUI } from "./loginUI";

type LoginMode = "magic" | "password" | "forgot";

export const LoginPage = observer(
  ({
    ui: uiOverrides,
  }: {
    /**
     * App-specific look for the form controls. Anything not provided falls
     * back to the core defaults, so passing a partial is fine.
     */
    ui?: Partial<LoginUI>;
  }) => {
    const brand = useBrand();
    const form = useLoginForm();
    const location = useLocation();
    // Password is the default. The reset page's "Get a new link" button lands
    // here with `?forgot=1`; a failed magic link lands with `?error=<code>`,
    // and its "send a fresh one" banner lives on the magic-link form.
    const [mode, setMode] = React.useState<LoginMode>(() => {
      if (wantsForgotForm(location.search)) {
        return "forgot";
      }
      if (getMagicLinkErrorMessage(new URLSearchParams(location.search))) {
        return "magic";
      }
      return "password";
    });

    // Memoised so the form components keep stable identities across
    // re-renders (a new component type each render would remount the inputs
    // and drop focus while typing).
    const ui = React.useMemo<LoginUI>(
      () => ({ ...defaultLoginUI, ...uiOverrides }),
      [uiOverrides],
    );

    // A message from one form must not follow the user onto another.
    function switchTo(next: LoginMode): void {
      form.clearMessages();
      setMode(next);
    }

    return (
      <UnauthLayout>
        <Image
          src={brand.logo}
          alt={`${brand.name} logo`}
          width={"120px"}
          mx="auto"
          mb={6}
        />

        {mode === "magic" && (
          <MagicLinkForm
            form={form}
            ui={ui}
            onUsePassword={() => switchTo("password")}
          />
        )}

        {mode === "password" && (
          <PasswordForm
            form={form}
            ui={ui}
            onUseMagicLink={() => switchTo("magic")}
            onForgotPassword={() => switchTo("forgot")}
          />
        )}

        {mode === "forgot" && (
          <ForgotPasswordForm
            form={form}
            ui={ui}
            onBackToLogin={() => switchTo("password")}
          />
        )}
      </UnauthLayout>
    );
  },
);
